import { useRef } from "react";
import { HorizBox, Pad } from "component/basics";
import { Pressable, ScrollView, StyleSheet, View } from "react-native";
import { DEPRECATED_EditorialHeading, UtilityText } from "component/text"
import { ChevronLeft, ChevronRight } from "@carbon/icons-react";
import { colorTealBackground, colorTextOnDarkBackground, colorTextSecondary } from "component/color"
import { TextButton } from "component/button"
import { useDatastore, useDerivedObject } from "util/datastore"
import { spacings } from "component/constants";
import { logEventAsync } from "util/eventlog";
import { CommentPreview } from "feature/teaser/editorial-question";
import { declareDerivedTable, t } from "util/stdtypes";
import { dropKey } from "util/util";

export type CommentPreviewData = {
    text: string;
    time: number;
    from: string;
    fromName: string | null;
    fromPhoto: string | null;
    fromHue: number | null;
};

export type RelatedQuestionPreview = {
    commentCount: number;
    time: number;
    commentPreview: CommentPreviewData | null;
    participantCount: number;
};

export const relatedQuestionPreviewTable = declareDerivedTable<RelatedQuestionPreview>('relatedQuestionPreview');

export function ConversationCard({
    text,
    singleCard = false,
    id,
    narrow = false,
}) {
    const questionPreview = useDerivedObject(relatedQuestionPreviewTable, id);
    const participantCount = questionPreview?.participantCount || 0;
    const formatParams = {count: participantCount, singular: 'participant', plural: 'participants'};
    const datastore = useDatastore();
    const s = createCardStyle({singleCard, narrow});
    
    return (
        <Pressable testID={`conversation-card-${id}`} style={s.card} onPress={() => {
            logEventAsync(datastore, 'related-conversation-click', {structureKey: 'question', instanceKey: id});
            datastore.gotoInstance({ structureKey: 'question', instanceKey: id });
        }}>
            <View>
                <DEPRECATED_EditorialHeading text={text} type={narrow ? "small" : "large"} color={colorTextOnDarkBackground} />
                <Pad size={spacings.xs} />
                <UtilityText label={t('{count} {noun}')} formatParams={formatParams} type='tiny' color={colorTextOnDarkBackground} />
            </View>
            <View style={s.spacer}>
                <CommentPreview commentPreview={questionPreview?.commentPreview ?? null} />
            </View>
        </Pressable>
    )
}

const createCardStyle = ({ singleCard, narrow }) =>
    StyleSheet.create({
        card: {
            flexDirection: (singleCard && !narrow) ? 'row' : 'column',
            justifyContent: (singleCard && !narrow) ? 'space-between' : 'flex-start',
            width: (singleCard && !narrow) ? '100%' : narrow ? 285 : 335,
            padding: spacings.lg,
            backgroundColor: colorTealBackground,
            borderRadius: 12,
        },
        spacer: {
            paddingTop: (singleCard && !narrow) ? 0 : spacings.lg,
        }
    })

function handleScrollTo({
    scrollRef,
    scrollViewMesurements,
    increment = false,
}) {
    const {x, width, cardWidth} = scrollViewMesurements;
    const position = increment ? Math.max(width, x + cardWidth) : Math.max(0, x - cardWidth);
    scrollRef.scrollTo({x: position, y: 0, animated: true})
}

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function RecirculationSlider({
    cardsData,
    controls = false,
    narrow = false,
}) {
    const scrollRef = useRef(null)
    const scrollViewMesurements = useRef({ x: 0, width: 0, cardWidth: 0});
    const s = recirculationSliderStyle;
    
    if(cardsData === undefined || cardsData?.length === 0) {
        return <></>;
    }
    
    if (cardsData?.length === 1) {
        return (
            <ConversationCard narrow={narrow} singleCard={true} {...dropKey(cardsData[0])} />
        )
    }

    return (
        <>
            <ScrollView ref={scrollRef} horizontal showsHorizontalScrollIndicator={false}
                onScroll={
                    ({nativeEvent}) => {
                        scrollViewMesurements.current = {...scrollViewMesurements.current, x: nativeEvent.contentOffset.x}
                    }
                }
                scrollEventThrottle={16}
                onLayout={
                    (event) => {
                        const { width } = event.nativeEvent.layout;
                        scrollViewMesurements.current = {...scrollViewMesurements.current, width, cardWidth: Math.round(width / cardsData.length)}
                    }
                }>
                {cardsData.map((props, index) =>
                    <View key={`ConversationCard-${props.id}`} style={s.container}>
                        <ConversationCard narrow={narrow} {...dropKey(props)} />
                        {cardsData?.length !== index + 1 && (
                            <Pad size={spacings.lg} />
                        )}
                    </View>
                )}
            </ScrollView>
            {controls && (
                <>
                    <Pad size={spacings.lg} />
                    <HorizBox spread center>
                        <TextButton label={t('Previous')} leftIcon={ChevronLeft} color={colorTextSecondary} onPress={() => handleScrollTo({scrollRef: scrollRef.current!, scrollViewMesurements: scrollViewMesurements.current})} />
                        <TextButton label={t('Next')} rightIcon={ChevronRight} color={colorTextSecondary} onPress={() => handleScrollTo({scrollRef: scrollRef.current!, scrollViewMesurements: scrollViewMesurements.current, increment: true})} />
                    </HorizBox>
                </>
            )}

        </>
    )
}

const recirculationSliderStyle = StyleSheet.create({
    container: {
        flexDirection: 'row',
    }
})
